import * as React from 'react';
import type { VariantProps } from 'tailwind-variants';

import { tv } from '../../lib/tv';

/**
 * The Figma Badge (Controls ↳ Badge, 48:340): radius/xs, 4px gap, Label/Small
 * at 20px (6px sides) or Label/Medium at 24px (8px sides). Fill, label and dot
 * colour per Tone × Style, read from the component set's bound variables.
 */
export const badgeVariants = tv({
  slots: {
    root: 'inline-flex w-fit shrink-0 items-center gap-1 rounded-xs whitespace-nowrap',
    dot: 'size-1.5 shrink-0 rounded-full',
  },
  variants: {
    tone: {
      neutral: {},
      brand: {},
      success: {},
      warning: {},
      danger: {},
      info: {},
    },
    variant: {
      subtle: {},
      // On solid badges the dot takes the label colour.
      solid: { dot: 'bg-current' },
    },
    size: {
      sm: { root: 'text-label-sm h-5 px-1.5' },
      md: { root: 'text-label-md h-6 px-2' },
    },
  },
  compoundVariants: [
    {
      tone: 'neutral',
      variant: 'subtle',
      class: { root: 'bg-bg-muted text-text-secondary', dot: 'bg-icon-secondary' },
    },
    {
      tone: 'brand',
      variant: 'subtle',
      class: { root: 'bg-brand-subtle text-text-brand', dot: 'bg-brand-strong' },
    },
    {
      tone: 'success',
      variant: 'subtle',
      class: {
        root: 'bg-status-success-bg text-status-success-fg',
        dot: 'bg-status-success-solid',
      },
    },
    {
      tone: 'warning',
      variant: 'subtle',
      class: {
        root: 'bg-status-warning-bg text-status-warning-fg',
        dot: 'bg-status-warning-solid',
      },
    },
    {
      tone: 'danger',
      variant: 'subtle',
      class: {
        root: 'bg-status-danger-bg text-status-danger-fg',
        dot: 'bg-status-danger-solid',
      },
    },
    {
      tone: 'info',
      variant: 'subtle',
      class: {
        root: 'bg-status-info-bg text-status-info-fg',
        dot: 'bg-status-info-solid',
      },
    },
    {
      tone: 'neutral',
      variant: 'solid',
      class: { root: 'bg-bg-inverse text-text-inverse' },
    },
    {
      tone: 'brand',
      variant: 'solid',
      class: { root: 'bg-brand-primary text-text-on-brand' },
    },
    {
      tone: 'success',
      variant: 'solid',
      class: { root: 'bg-status-success-solid text-text-inverse' },
    },
    {
      tone: 'warning',
      variant: 'solid',
      class: { root: 'bg-status-warning-solid text-text-primary' },
    },
    {
      tone: 'danger',
      variant: 'solid',
      class: { root: 'bg-status-danger-solid text-text-inverse' },
    },
    {
      tone: 'info',
      variant: 'solid',
      class: { root: 'bg-status-info-solid text-text-inverse' },
    },
  ],
  defaultVariants: { tone: 'neutral', variant: 'subtle', size: 'sm' },
});

/** shadcn Badge, restyled to the Figma Badge. Apps use `Badge` (components/lib). */
function Badge({
  className,
  tone,
  variant,
  size,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants>) {
  return (
    <span
      data-slot="badge"
      className={badgeVariants({ tone, variant, size }).root({ className })}
      {...props}
    />
  );
}

export { Badge };
